/**
 * O resumo "Saldo por colaborador" do banco de horas.
 *
 * Papel: reproduz `bhRenderDashboard` da origem — o bloco com o título, a data
 * "ATUALIZADO ATÉ", os botões de importar, imprimir e cadastrar, e a tabela com
 * o nome e a função, o saldo num selo da cor da faixa e o saldo em dias.
 *
 * Como usar: quem monta passa a lista inteira do banco de horas, e é aqui que
 * ela vira só os ativos em ordem de nome, por `ativosPorNome`. Cada botão só
 * aparece com a ação dele ligada — quem não pode gravar recebe só `onImprimir`.
 *
 * A linha inteira abre o colaborador, pelo clique e, no teclado, por Enter ou
 * Espaço: ela leva `tabIndex`, e nada muda na aparência.
 *
 * Os três botões levam a entrelinha `normal` (`text-sm/[normal]`), a do `.btn`
 * da origem: os dois com emoji crescem com ele, e o "+ Novo colaborador" estica
 * até a mesma altura, como lá. Em tela estreita eles encolhem e quebram o texto
 * (`shrink`, `whitespace-normal`), como o `.btn`; com o `Button` como vem, o
 * "+ Novo colaborador" sairia da tela a 375px.
 *
 * Os três cabeçalhos ordenam a tabela pela regra `'com-retorno'` de
 * `useOrdenacao`: crescente, decrescente e de volta à ordem de nome de
 * `ativosPorNome`. "Colaborador" ordena pelo `nomeCompleto`; "Saldo" e
 * "Dias (aprox.)" ordenam os dois pelo saldo em minutos — os dias são o saldo
 * arredondado, e dois saldos diferentes que dão os mesmos dias continuam na
 * ordem do saldo.
 */
import { type ColaboradorBancoHoras, faixaDoSaldo, saldoEmMinutos } from '@concierge/shared';
import { type KeyboardEvent, useMemo } from 'react';

import {
  CabecalhoOrdenavel,
  type ColunaOrdenavel,
  SETA_EM_TEXTO,
  useOrdenacao,
} from '@/shared/embutido';
import { formatarData } from '@/shared/lib/formato';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { CabecalhoDeSecao } from '@/shared/ui/cabecalho-de-secao';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { VARIANTE_DA_FAIXA, ativosPorNome, formatarDias, formatarHoras } from '../banco-horas';

interface ResumoBancoHorasProps {
  /** A data "atualizado até", em ISO, ou `null`. */
  atualizacao: string | null;
  /** A lista inteira do banco de horas; os inativos ficam de fora aqui. */
  colaboradores: readonly ColaboradorBancoHoras[];
  /** Chamado com o `id` da linha clicada. */
  onAbrir: (id: string) => void;
  /** Abre a importação do extrato; ausente esconde o botão. */
  onImportar?: () => void;
  /** Imprime o resumo; ausente esconde o botão. */
  onImprimir?: () => void;
  /** Abre a caixa de colaborador novo; ausente esconde o botão. */
  onNovo?: () => void;
}

/** As colunas ordenáveis; "Dias (aprox.)" compara o mesmo saldo que "Saldo". */
const COLUNAS: Record<string, ColunaOrdenavel<ColaboradorBancoHoras>> = {
  nome: { tipo: 'texto', valor: (c) => c.nomeCompleto },
  saldo: { tipo: 'numero', valor: (c) => saldoEmMinutos(c.meses) },
  dias: { tipo: 'numero', valor: (c) => saldoEmMinutos(c.meses) },
};

/**
 * O resumo do banco de horas.
 *
 * @param atualizacao - A data do subtítulo; `null` escreve `—`.
 * @param colaboradores - A lista inteira, ativos e inativos.
 * @param onAbrir - Chamado ao clicar numa linha ou apertar Enter nela.
 * @param onImportar - O "📥 Importar Secullum".
 * @param onImprimir - O "🖨 Imprimir / PDF".
 * @param onNovo - O "+ Novo colaborador".
 * @returns O bloco com o cabeçalho e a tabela.
 */
export function ResumoBancoHoras({
  atualizacao,
  colaboradores,
  onAbrir,
  onImportar,
  onImprimir,
  onNovo,
}: ResumoBancoHorasProps) {
  const ativos = useMemo(() => ativosPorNome(colaboradores), [colaboradores]);
  const ordenacao = useOrdenacao(ativos, COLUNAS, 'com-retorno');
  const temBotao =
    onImportar !== undefined || onImprimir !== undefined || onNovo !== undefined;

  /** Abre a linha pelo teclado, como o clique. */
  function handleTecla(evento: KeyboardEvent, id: string) {
    if (evento.key !== 'Enter' && evento.key !== ' ') return;
    evento.preventDefault();
    onAbrir(id);
  }

  return (
    <section className="mb-9">
      <CabecalhoDeSecao
        titulo="Saldo por colaborador"
        subtitulo={`ATUALIZADO ATÉ ${formatarData(atualizacao)}`}
        acoes={
          temBotao ? (
            <div className="flex gap-2.5">
              {onImportar !== undefined && (
                <Button
                  variant="outline"
                  className="shrink text-center text-sm/[normal] whitespace-normal"
                  onClick={onImportar}
                >
                  📥 Importar Secullum
                </Button>
              )}
              {onImprimir !== undefined && (
                <Button
                  variant="outline"
                  className="shrink text-center text-sm/[normal] whitespace-normal"
                  onClick={onImprimir}
                >
                  🖨 Imprimir / PDF
                </Button>
              )}
              {onNovo !== undefined && (
                <Button
                  className="shrink text-center text-sm/[normal] whitespace-normal"
                  onClick={onNovo}
                >
                  + Novo colaborador
                </Button>
              )}
            </div>
          ) : undefined
        }
      />

      <Table>
        <TableHeader>
          <TableRow>
            <CabecalhoOrdenavel
              como={TableHead}
              title="Ordenar por Colaborador"
              seta={ordenacao.seta('nome')}
              classeDaSeta={SETA_EM_TEXTO}
              onAlternar={() => ordenacao.alternar('nome')}
            >
              Colaborador
            </CabecalhoOrdenavel>
            <CabecalhoOrdenavel
              como={TableHead}
              className="text-right"
              title="Ordenar por Saldo"
              seta={ordenacao.seta('saldo')}
              classeDaSeta={SETA_EM_TEXTO}
              onAlternar={() => ordenacao.alternar('saldo')}
            >
              Saldo
            </CabecalhoOrdenavel>
            <CabecalhoOrdenavel
              como={TableHead}
              className="text-right"
              title="Ordenar por Dias (aprox.)"
              seta={ordenacao.seta('dias')}
              classeDaSeta={SETA_EM_TEXTO}
              onAlternar={() => ordenacao.alternar('dias')}
            >
              Dias (aprox.)
            </CabecalhoOrdenavel>
          </TableRow>
        </TableHeader>
        <TableBody>
          {ordenacao.linhas.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="p-6 text-center text-texto-fraco">
                Nenhum colaborador cadastrado.
              </TableCell>
            </TableRow>
          ) : (
            ordenacao.linhas.map((colaborador) => {
              const saldo = saldoEmMinutos(colaborador.meses);
              return (
                <TableRow
                  key={colaborador.id}
                  className="cursor-pointer"
                  tabIndex={0}
                  onClick={() => onAbrir(colaborador.id)}
                  onKeyDown={(evento) => handleTecla(evento, colaborador.id)}
                >
                  <TableCell className="text-texto-medio">
                    <span className="font-medium text-foreground">
                      {colaborador.nomeCompleto}
                    </span>
                    <div className="mt-0.5 text-[11px] text-texto-fraco">
                      {colaborador.funcao ?? '—'}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={VARIANTE_DA_FAIXA[faixaDoSaldo(saldo)]}>
                      {formatarHoras(saldo)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-texto-medio">
                    {formatarDias(saldo)}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </section>
  );
}
